import {
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    inject,
    Injector,
    Input,
    OnDestroy,
    Renderer2,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
    BaseNestedFieldDefinition,
    FieldType,
    getParentDottedKey
} from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { AIProfileField, ProfileField } from '../../../../core/profiles/entities/profile-field.entity';
import { Profile } from '../../../../core/profiles/entities/profile.entity';
import { Family } from '../../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../../core/records/entities/partial-record.class';
import { ShInjectionPointDirective } from '../../../directives/injection-point.directive';
import { ShProfileConfigService } from '../../../services/profile-config.service';
import { ShBaseGenericReadonlyFieldComponent } from '../base-generic-readonly-field/base-generic-readonly-field.component';
import { ShUnsupportedReadonlyFieldComponent } from '../simple-readonly-fields/unsupported-readonly-field/unsupported-readonly-field.component';

export type GenericReadonlyFieldDescriptor = {
    type: Type<ShBaseGenericReadonlyFieldComponent>;
    config?: Record<string, unknown>;
};

type InputTypes = [ExtendedBaseProfileField<BaseNestedFieldDefinition>, PartialRecord, number];
@Component({
    standalone: false,
    selector: 'sh-generic-readonly-field',
    template: `<ng-template injection-point></ng-template>`,
    styleUrls: ['../base-generic-readonly-field/base-generic-readonly-field.component.scss']
})
export class ShGenericReadonlyFieldComponent implements OnDestroy {
    private componentRef!: ComponentRef<ShBaseGenericReadonlyFieldComponent>;

    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly initialized$: Subject<boolean> = new Subject<boolean>();
    private readonly record$: Subject<PartialRecord> = new Subject();
    private readonly profileField$: Subject<ExtendedBaseProfileField> = new Subject();
    private readonly aiProfile$: BehaviorSubject<Profile | undefined> = new BehaviorSubject<Profile | undefined>(
        undefined
    );
    private readonly nestedIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    @Input() set profileField(profileField: ExtendedBaseProfileField) {
        this.profileField$.next(profileField);
    }

    @Input() set aiProfile(profile: Profile) {
        if (profile) {
            this.aiProfile$.next(profile);
        }
    }

    @Input() set record(record: PartialRecord) {
        this.record$.next(record);
    }

    @Input() nestedIndex(nestedIndex: number) {
        this.nestedIndex$.next(nestedIndex);
    }

    @ViewChild(ShInjectionPointDirective, { static: true }) adHost!: ShInjectionPointDirective;

    private readonly el: ElementRef = inject(ElementRef);
    private readonly renderer: Renderer2 = inject(Renderer2);

    constructor(
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly profileConfigService: ShProfileConfigService
    ) {
        this.setup();
    }

    private setup(): void {
        const sources$ = combineLatest([
            this.profileField$,
            this.record$,
            this.nestedIndex$.pipe(take(1)),
            this.profileConfigService.getReadonlyOverrides()
        ]);

        // Wait for all sources to emit before initializing the child component
        sources$.pipe(takeUntil(this.destroy$)).subscribe(([profileField, record, nestedIndex, overrides]) => {
            this.destroyExisting();
            this.initChildComponent(profileField, record, nestedIndex, overrides);
        });

        this.initIconConditions();
    }

    ngOnDestroy(): void {
        this.nestedIndex$.complete();
        this.destroyExisting();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private destroyExisting(): void {
        this.componentRef?.destroy();
    }

    private initChildComponent(
        profileField: ExtendedBaseProfileField,
        record: PartialRecord,
        nestedIndex: number,
        overrides: Record<string, GenericReadonlyFieldDescriptor>
    ): void {
        const defaultMapping = this.profileConfigService.getDefaultReadOnlyMappings();

        const dottedKey = profileField.FieldDefinition.DottedKey;
        const fieldType = profileField.FieldDefinition.Type;
        const controlOverride = profileField.Control;

        const { componentType, componentConfig } = this.resolveReadonlyComponent(
            overrides,
            dottedKey,
            fieldType,
            controlOverride,
            defaultMapping
        );

        const typeToLoad = componentType ?? ShUnsupportedReadonlyFieldComponent;
        if ((typeToLoad as typeof ShBaseGenericReadonlyFieldComponent).IS_COMPLEX) {
            this.renderer.addClass(this.el.nativeElement, 'sh-complex-field');
        }

        return this.loadChildComponent(typeToLoad, componentConfig, profileField, record, nestedIndex);
    }

    private resolveReadonlyComponent(
        overrides: Record<string, GenericReadonlyFieldDescriptor>,
        dottedKey: string,
        fieldType: FieldType,
        controlOverride: string | undefined,
        defaultMapping: Record<FieldType, Type<ShBaseGenericReadonlyFieldComponent>>
    ): { componentType?: Type<ShBaseGenericReadonlyFieldComponent>; componentConfig: Record<string, any> } {
        if (controlOverride) {
            const parsed = this.parseControlOverride(controlOverride);
            if (typeof parsed === 'string') {
                return {
                    componentType:
                        this.profileConfigService.overrideReadonlyMapping[parsed] ?? defaultMapping[fieldType],
                    componentConfig: {}
                };
            }
            if (
                parsed &&
                Object.prototype.hasOwnProperty.call(parsed, 'type') &&
                Object.prototype.hasOwnProperty.call(parsed, 'config')
            ) {
                return {
                    componentType:
                        this.profileConfigService.overrideReadonlyMapping[parsed.type] ?? defaultMapping[fieldType],
                    componentConfig: parsed.config ?? {}
                };
            }
        }

        const override = overrides?.[dottedKey];
        return {
            componentType: override?.type ?? defaultMapping[fieldType],
            componentConfig: override?.config ?? {}
        };
    }

    private parseControlOverride(controlOverride: string): any {
        try {
            return JSON.parse(controlOverride);
        } catch {
            return controlOverride;
        }
    }

    private loadChildComponent(
        componentType: Type<ShBaseGenericReadonlyFieldComponent>,
        config: Record<string, unknown>,
        profileField: ExtendedBaseProfileField,
        record: PartialRecord,
        nestedIndex: number
    ): void {
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: 'config',
                    useValue: config
                }
            ]
        });

        const componentFactory: ComponentFactory<any> =
            this.componentFactoryResolver.resolveComponentFactory(componentType);

        const viewContainerRef: ViewContainerRef = this.adHost.viewContainerRef;
        viewContainerRef.clear();

        this.componentRef = viewContainerRef.createComponent(componentFactory, undefined, injector);

        this.setPartialRecord(record);
        this.setProfileField(profileField);
        this.setNestedIndex(nestedIndex);

        this.initialized$.next(true);
    }

    private setPartialRecord(record: PartialRecord): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.record = record;
        }
    }

    private setProfileField(profileField: ExtendedBaseProfileField): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.profileField = profileField;
        }
    }

    private setNestedIndex(nestedIndex: number): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.nestedIndex = nestedIndex;
        }
    }

    // #region AI indicator
    private initIconConditions() {
        combineLatest([this.initialized$, this.profileField$, this.aiProfile$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([initialized, profileField, aiProfile]) => {
                if (
                    profileField.FieldDefinition.Family === Family.Ai ||
                    aiProfile?.Fields.find(aiField =>
                        this.matchingAIEnhancedField(profileField, aiField as AIProfileField)
                    )
                ) {
                    this.componentRef.instance.aiActive = true;
                }
            });
    }

    private matchingAIEnhancedField(profileField: ProfileField, aiProfileField: AIProfileField): boolean {
        const isAIEnhancedField: boolean = aiProfileField.GenerativeMetadata.Hint !== null;
        const isMatchingField: boolean =
            aiProfileField.DottedKey === profileField.DottedKey ||
            getParentDottedKey(aiProfileField.DottedKey) === profileField.DottedKey; // Needed for listfields

        return isAIEnhancedField && isMatchingField;
    }
    // #endregion
}
